import { marked } from 'marked'
import type { Tokens, Token } from 'marked'
import YAML from 'yaml'
import type { RenderOptions, RenderData } from '../assetTypes'
import { collectAssetReferencesFromRenderData } from '../shared/assetReferences'

type FrontMatterResult = {
  markdown: string
  metadata?: Record<string, unknown>
  metadataError?: string
}

type HeadingInfo = {
  text: string
  imageHref?: string
  imageMissing?: boolean
}

type SectionTheme = {
  id: string
  emoji: string
  background: string
  simple: boolean
}

const DEFAULT_MESSAGE = 'Nothing to render yet.'
const DEFAULT_SECTION_BACKGROUND = '#AAAAAA'

const SECTION_THEMES: SectionTheme[] = [
  { id: 'LessonOverview', emoji: '\uD83D\uDDC2', background: '#E5CBCB', simple: true },
  { id: 'Introduction', emoji: '\uD83D\uDDE3', background: '#CBE5D6', simple: false },
  { id: 'SuggestedLearnerActivity', emoji: '\uD83D\uDCDD', background: '#FDDDF1', simple: false },
  { id: 'MainActivity', emoji: '\u270D\uD83C\uDFFE', background: '#D0E0FF', simple: false },
  { id: 'Conclusion', emoji: '\uD83C\uDFC1', background: '#DBD0FF', simple: false },
]

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const serializeForScript = (value: unknown) =>
  JSON.stringify(value ?? null)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')

const extractFrontMatter = (raw: string): FrontMatterResult => {
  const lines = raw.split(/\r?\n/)
  let startIndex = 0
  while (startIndex < lines.length && lines[startIndex].trim() === '') startIndex += 1
  if (lines[startIndex]?.trim() !== '---') return { markdown: raw }

  const endIndex = lines.findIndex((line, index) => index > startIndex && line.trim() === '---')
  if (endIndex === -1) return { markdown: raw }

  const frontMatterRaw = lines.slice(startIndex + 1, endIndex).join('\n')
  const markdown = lines.slice(endIndex + 1).join('\n')

  if (!frontMatterRaw.trim()) return { markdown, metadata: {} }

  try {
    const parsed = YAML.parse(frontMatterRaw)
    if (parsed && typeof parsed === 'object') return { markdown, metadata: parsed as Record<string, unknown> }
    return { markdown, metadata: { value: parsed } }
  } catch (error) {
    return {
      markdown,
      metadataError: `Failed to parse lesson metadata: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
}

const toKebabCase = (value: string) => value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

const resolveSectionTheme = (title: string) => {
  const trimmed = title.trimStart()
  return SECTION_THEMES.find((entry) => trimmed.startsWith(entry.emoji))
}

const extractHeadingInfo = (heading: Tokens.Heading): HeadingInfo | null => {
  let text = ''
  let imageHref: string | undefined
  let imageMissing = false

  for (const token of heading.tokens ?? []) {
    if (!text && token.type === 'text') text = token.text
    if (!imageHref && token.type === 'image') {
      const imageToken = token as Tokens.Image & { trellisMissing?: boolean }
      imageHref = imageToken.href
      imageMissing = Boolean(imageToken.trellisMissing)
    }
    if (text && imageHref) break
  }

  if (!text) text = heading.text ?? ''
  if (!text.trim()) return null

  return {
    text: text.trim(),
    imageHref: imageHref || undefined,
    imageMissing,
  }
}

const buildMarkdownRenderer = (content: string, options?: RenderOptions) => {
  const raw = content && content.trim().length ? content : DEFAULT_MESSAGE
  const { markdown, metadata, metadataError } = extractFrontMatter(raw)

  const assetIds = new Set<string>()

  const tokens = marked.lexer(markdown, { gfm: true })

  marked.walkTokens(tokens, (token) => {
    if (token.type !== 'image' && token.type !== 'link') return
    const assetToken = token as (Tokens.Image | Tokens.Link) & {
      trellisAssetId?: string
      trellisAssetKind?: 'image' | 'link'
      trellisAssetLabel?: string
      trellisAssetTitle?: string
    }
    if (!assetToken.href || !assetToken.href.startsWith('asset://')) return
    const assetId = assetToken.href.slice('asset://'.length)
    if (!assetId) return
    assetIds.add(assetId)
    assetToken.trellisAssetId = assetId
    assetToken.trellisAssetKind = token.type === 'image' ? 'image' : 'link'
    assetToken.trellisAssetLabel = assetToken.text ?? ''
    assetToken.trellisAssetTitle = assetToken.title ?? ''
  })

  const renderer = new marked.Renderer()
  renderer.image = (token) => {
    const imageToken = token as Tokens.Image & {
      trellisAssetId?: string
      trellisAssetKind?: 'image' | 'link'
      trellisAssetLabel?: string
      trellisAssetTitle?: string
    }
    const href = imageToken.href ?? ''
    const title = imageToken.title ? ` title="${escapeHtml(imageToken.title)}"` : ''
    const alt = escapeHtml(imageToken.text ?? '')

    if (imageToken.trellisAssetId) {
      const safeLabel = escapeHtml(imageToken.trellisAssetLabel || '')
      const safeTitle = imageToken.trellisAssetTitle ? escapeHtml(imageToken.trellisAssetTitle) : ''
      const titleAttr = safeTitle ? ` data-asset-title="${safeTitle}"` : ''
      return `
        <span class="lesson-asset" data-asset-id="${escapeHtml(imageToken.trellisAssetId)}"
          data-asset-kind="image" data-asset-label="${safeLabel}"${titleAttr}>
          ${safeLabel || 'Loading asset...'}
        </span>
      `
    }

    if (!href) {
      const fallback = alt || 'Missing image'
      return `<span class="lesson-image-missing">${fallback}</span>`
    }

    return `<img src="${escapeHtml(href)}" alt="${alt}"${title} />`
  }

  renderer.link = (token) => {
    const linkToken = token as Tokens.Link & {
      trellisAssetId?: string
      trellisAssetKind?: 'image' | 'link'
      trellisAssetLabel?: string
      trellisAssetTitle?: string
    }
    const href = linkToken.href ?? ''
    const text = linkToken.text ?? ''
    const title = linkToken.title ? ` title="${escapeHtml(linkToken.title)}"` : ''
    if (linkToken.trellisAssetId) {
      const safeLabel = escapeHtml(linkToken.trellisAssetLabel || '')
      const safeTitle = linkToken.trellisAssetTitle ? escapeHtml(linkToken.trellisAssetTitle) : ''
      const titleAttr = safeTitle ? ` data-asset-title="${safeTitle}"` : ''
      return `
        <span class="lesson-asset" data-asset-id="${escapeHtml(linkToken.trellisAssetId)}"
          data-asset-kind="link" data-asset-label="${safeLabel}"${titleAttr}>
          ${safeLabel || 'Loading asset...'}
        </span>
      `
    }

    const label = text ? escapeHtml(text) : escapeHtml(href)
    return `<a href="${escapeHtml(href)}"${title}>${label}</a>`
  }

  const introTokens: Token[] = []
  const sections: Array<{ heading: Tokens.Heading; tokens: Token[] }> = []
  let currentSection: { heading: Tokens.Heading; tokens: Token[] } | null = null

  tokens.forEach((token) => {
    if (token.type === 'heading' && token.depth === 3) {
      currentSection = { heading: token as Tokens.Heading, tokens: [] }
      sections.push(currentSection)
    } else if (currentSection) {
      currentSection.tokens.push(token)
    } else {
      introTokens.push(token)
    }
  })

  const parseTokens = (inputTokens: Token[]) => marked.parser(inputTokens, { renderer })
  const introHtml = introTokens.length ? parseTokens(introTokens) : ''

  const sectionsHtml = sections
    .map((section) => {
      const headingInfo = extractHeadingInfo(section.heading)
      const contentHtml = section.tokens.length ? parseTokens(section.tokens) : ''

      if (!headingInfo) {
        return `<div class="lesson-content lesson-content--fancy">${contentHtml}</div>`
      }

      const theme = resolveSectionTheme(headingInfo.text)
      const background = theme?.background ?? DEFAULT_SECTION_BACKGROUND
      const sectionClass = theme ? `lesson-section--${toKebabCase(theme.id)}` : 'lesson-section--default'
      const contentClass = theme?.simple ? 'lesson-content--simple' : 'lesson-content--fancy'
      const headingImage =
        headingInfo.imageHref && !headingInfo.imageMissing
          ? `<img class="lesson-heading-image" src="${escapeHtml(headingInfo.imageHref)}" alt="" />`
          : ''

      return `
        <section class="lesson-section ${sectionClass}">
          <div class="lesson-heading" style="background: ${background}">
            ${headingImage}
            <div class="lesson-heading-text">${escapeHtml(headingInfo.text)}</div>
          </div>
          <div class="lesson-content ${contentClass}">${contentHtml}</div>
        </section>
      `
    })
    .join('')

  const metadataJson = metadata ? JSON.stringify(metadata, null, 2) : ''
  const serializedMetadata = serializeForScript(metadata)
  const serializedMetadataError = serializeForScript(metadataError ?? '')
  const serializedMetadataText = serializeForScript(metadataJson)
  const serializedRenderData = serializeForScript(options?.data)
  const serializedAssetIds = serializeForScript([...assetIds])
  const serializedAssetCount = serializeForScript(assetIds.size)

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      :root {
        color-scheme: light;
        --lesson-font: 'Quicksand', 'Nunito', 'Varela Round', 'Avenir', 'Segoe UI', sans-serif;
        --lesson-text: #29395F;
        --lesson-bg: #ffffff;
        --lesson-block-margin: 30px;
        --lesson-font-size: 18px;
      }
      * {
        box-sizing: border-box;
      }
      body {
        margin: 0;
        font-family: var(--lesson-font);
        background-color: var(--lesson-bg);
        color: var(--lesson-text);
        overflow: auto;
      }
      .lesson-shell {
        min-height: 100vh;
        background: var(--lesson-bg);
      }
      .lesson-heading {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 48px;
        padding: 8px;
        color: #29395F;
        font-family: var(--lesson-font);
        font-weight: 700;
        font-size: 18px;
      }
      .lesson-heading-text {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .lesson-heading-image {
        height: 30px;
        width: auto;
        object-fit: contain;
      }
      .lesson-content {
        padding: 25px 21px;
        font-size: var(--lesson-font-size);
        line-height: 1.35;
        font-weight: 500;
      }
      .lesson-content h1 {
        font-size: calc(var(--lesson-font-size) * 1.8);
      }
      .lesson-content h2 {
        font-size: calc(var(--lesson-font-size) * 1.5);
      }
      .lesson-content h3 {
        font-size: calc(var(--lesson-font-size) * 1.3);
      }
      .lesson-content h4 {
        font-size: calc(var(--lesson-font-size) * 1.2);
      }
      .lesson-content h5 {
        font-size: calc(var(--lesson-font-size) * 1.1);
      }
      .lesson-content h6 {
        font-size: calc(var(--lesson-font-size) * 1);
      }
      .lesson-content h1,
      .lesson-content h2,
      .lesson-content h3,
      .lesson-content h4,
      .lesson-content h5,
      .lesson-content h6 {
        margin: 20px 0 12px;
        font-weight: 700;
      }
      .lesson-content p {
        margin: 0 0 12px;
      }
      .lesson-content p:last-child {
        margin-bottom: 0;
      }
      .lesson-content a {
        color: #2A68C6;
        text-decoration: underline;
      }
      .lesson-content ul,
      .lesson-content ol {
        margin: 8px 0;
        padding-left: 28px;
      }
      .lesson-content li {
        margin-bottom: 6px;
      }
      .lesson-content--fancy > ol {
        list-style: none;
        padding-left: 0;
        margin: 12px 0;
        counter-reset: lesson-ol;
      }
      .lesson-content--fancy > ol > li {
        position: relative;
        padding-left: 44px;
        margin-bottom: 12px;
        padding-bottom: 10px;
        border-bottom: 1px solid #D4D4D4;
        counter-increment: lesson-ol;
      }
      .lesson-content--fancy > ol > li:last-child {
        border-bottom: none;
        padding-bottom: 0;
        margin-bottom: 0;
      }
      .lesson-content--fancy > ol > li::before {
        content: counter(lesson-ol);
        position: absolute;
        left: 0;
        top: 0.15em;
        width: 26px;
        height: 26px;
        border-radius: 999px;
        background: #29395F;
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
      }
      .lesson-content img {
        max-width: calc(100% - var(--lesson-block-margin));
        margin: 12px 0 12px var(--lesson-block-margin);
        height: auto;
        display: block;
        border-radius: 2px;
      }
      .lesson-audio,
      .lesson-video {
        margin: 12px 0 12px var(--lesson-block-margin);
        max-width: calc(100% - var(--lesson-block-margin));
      }
      .lesson-audio {
        display: inline-flex;
        align-items: center;
      }
      .lesson-audio audio {
        display: none;
      }
      .lesson-audio-btn {
        width: 36px;
        height: 36px;
        border-radius: 999px;
        border: none;
        background: #2A68C6;
        cursor: pointer;
        position: relative;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
      }
      .lesson-audio-btn::before {
        content: '';
        position: absolute;
        left: 14px;
        top: 10px;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 8px 0 8px 12px;
        border-color: transparent transparent transparent #ffffff;
      }
      .lesson-audio-btn::after {
        content: '';
        position: absolute;
        left: 12px;
        top: 10px;
        width: 4px;
        height: 16px;
        background: #ffffff;
        box-shadow: 8px 0 0 #ffffff;
        display: none;
      }
      .lesson-audio[data-state='playing'] .lesson-audio-btn::before {
        display: none;
      }
      .lesson-audio[data-state='playing'] .lesson-audio-btn::after {
        display: block;
      }
      .lesson-video video {
        width: 100%;
        height: auto;
        border-radius: 4px;
        background: #000000;
      }
      .lesson-content blockquote {
        margin: 12px 0;
        padding: 12px 16px;
        background: #EAF0F9;
        border-left: 4px solid #2A68C6;
      }
      .lesson-content blockquote > *:last-child {
        margin-bottom: 0;
      }
      .lesson-content hr {
        border: none;
        border-top: 1px solid #D4D4D4;
        margin: 12px 0;
      }
      .lesson-content table {
        border-collapse: collapse;
        width: 100%;
        margin: 12px 0;
        font-size: 0.95em;
      }
      .lesson-content th,
      .lesson-content td {
        border: 1px solid #D4D4D4;
        padding: 6px 8px;
        text-align: left;
        vertical-align: top;
      }
      .lesson-content th {
        background: #F6F6F6;
        font-weight: 700;
      }
      .lesson-content code {
        font-family: 'JetBrains Mono', 'Fira Code', monospace;
        font-size: 0.95em;
        background: #F6F6F6;
        padding: 0 4px;
        border-radius: 4px;
      }
      .lesson-content pre {
        background: #F6F6F6;
        padding: 12px;
        border-radius: 8px;
        overflow-x: auto;
      }
      .lesson-content pre code {
        background: transparent;
        padding: 0;
      }
      .lesson-image-missing {
        display: inline-block;
        padding: 6px 10px;
        border: 1px dashed #D97706;
        background: #FFF7ED;
        color: #B45309;
        border-radius: 6px;
      }
      .lesson-asset {
        display: inline-block;
        padding: 4px 8px;
        border: 1px dashed #cbd5f5;
        background: #f8fafc;
        color: #64748b;
        border-radius: 6px;
        font-size: 0.9em;
      }
    </style>
  </head>
  <body>
    <div class="lesson-shell">
      ${introHtml ? `<div class="lesson-content lesson-content--fancy">${introHtml}</div>` : ''}
      ${sectionsHtml}
    </div>
    <script>
      const trellis = window.trellis
      const renderLog = (message) => {
        trellis.log(message)
      }
      renderLog('Render runtime initialized')
      const metadata = ${serializedMetadata}
      const metadataError = ${serializedMetadataError}
      const metadataText = ${serializedMetadataText}
      const renderData = ${serializedRenderData}
      const assetIds = ${serializedAssetIds}
      const assetCount = ${serializedAssetCount}

      if (metadataError) {
        renderLog(metadataError)
      } else if (metadata && Object.keys(metadata).length > 0) {
        renderLog('Lesson metadata:\\n' + metadataText)
      } else {
        renderLog('No lesson metadata found.')
      }

      const escapeHtml = (value) =>
        String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;')

      const assetApi = trellis.assets
      const getAssetHandle = (assetId) => {
        try {
          return assetApi.get('asset://' + assetId)
        } catch (error) {
          return undefined
        }
      }

      const attachAudioHandlers = () => {
        document.querySelectorAll('.lesson-audio').forEach((wrapper) => {
          const button = wrapper.querySelector('button')
          const audio = wrapper.querySelector('audio')
          if (!button || !audio) return
          const updateState = () => {
            wrapper.setAttribute('data-state', audio.paused ? 'paused' : 'playing')
          }
          button.addEventListener('click', () => {
            if (audio.paused) {
              audio.play().catch((error) => {
                renderLog('Audio play failed: ' + (error && error.message ? error.message : String(error)))
              })
            } else {
              audio.pause()
            }
          })
          audio.addEventListener('play', updateState)
          audio.addEventListener('pause', updateState)
          audio.addEventListener('ended', updateState)
        })
      }

      const replaceAssets = async () => {
        if (!assetIds || assetIds.length === 0) {
          renderLog('No lesson assets referenced.')
          return
        }
        renderLog('Resolving lesson assets: ' + assetCount)

        const missing = new Set()
        const placeholders = Array.from(document.querySelectorAll('.lesson-asset'))

        for (const placeholder of placeholders) {
          const assetId = placeholder.getAttribute('data-asset-id') || ''
          if (!assetId) continue
          const assetInfo = getAssetHandle(assetId)
          if (!assetInfo) {
            missing.add(assetId)
            placeholder.classList.add('lesson-image-missing')
            placeholder.textContent = placeholder.getAttribute('data-asset-label') || 'Missing asset'
            continue
          }

          const kind = placeholder.getAttribute('data-asset-kind') || 'image'
          const label = placeholder.getAttribute('data-asset-label') || ''
          if (assetInfo.mediaType === 'audio') {
            placeholder.outerHTML =
              '<div class="lesson-audio" data-state="paused">' +
              '<button class="lesson-audio-btn" type="button" aria-label="Play audio"></button>' +
              '<audio preload="none" src="' +
              assetInfo.url +
              '"></audio>' +
              '</div>'
            continue
          }
          if (assetInfo.mediaType === 'video') {
            const mimeAttr = assetInfo.mimeType ? ' type="' + assetInfo.mimeType + '"' : ''
            placeholder.outerHTML =
              '<div class="lesson-video">' +
              '<video controls playsinline preload="metadata">' +
              '<source src="' +
              assetInfo.url +
              '"' +
              mimeAttr +
              ' />' +
              '</video>' +
              '</div>'
            continue
          }
          if (kind === 'link') {
            const safeLabel = label ? escapeHtml(label) : assetInfo.url
            placeholder.outerHTML = '<a href="' + assetInfo.url + '">' + safeLabel + '</a>'
            continue
          }
          const alt = escapeHtml(label || 'Lesson asset')
          placeholder.outerHTML = '<img src="' + assetInfo.url + '" alt="' + alt + '" />'
        }

        attachAudioHandlers()
        if (missing.size > 0) {
          renderLog('Missing lesson assets: ' + Array.from(missing).join(', '))
        }
      }

      replaceAssets().catch((error) => {
        renderLog('Asset render failed: ' + (error && error.message ? error.message : String(error)))
      })
    </script>
  </body>
</html>`
}

export default buildMarkdownRenderer

export const collectAssetReferences = (content: string, renderData?: RenderData) =>
  collectAssetReferencesFromRenderData(content, renderData)
